import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Icon } from '@grandvisionhq/icons';
import LoaderScreen from '../Loader/LoaderScreen.js';
import { usePrevious } from '../Util/use-previous.js';
import 'lodash/uniq';
import '@grandvisionhq/utils';
import '../MediaQuery/MediaQueryContext.js';
import '../Util/get-donation-foundation-icon.js';
import { isEqualPositionsAndOrder } from '../Util/is-equal-positions-and-order.js';
import { useGoogleMapsModule } from './external-module.js';
import { useGoogleMapsContext } from './GoogleMapsContext/GoogleMapsContext.js';
import { getMarkerProps } from './utils/get-marker-props.js';

const MAX_MARKERS_AMOUNT = 50;
const DEFAULT_MAX_ZOOM = 16;
const DEFAULT_SHARE_CURRENT_LOCATION_LABEL = 'Share current location';
const DEFAULT_ZOOM_IN_LABEL = 'Zoom in';
const DEFAULT_ZOOM_OUT_LABEL = 'Zoom out';
const GoogleMaps = ({ showUseMyLocation =false , onClickUseMyLocation , userLocation: userLocation1 , userLocationZoomLevel , centerOnUserLocation =false , highlightMarkerId , hovered , markers , onMarkerClick , onMarkerHover , onMapClick , mapOptions , showLoader =true , center: centerProp , markersUpdateConfig , labels , id ,  })=>{
    const { googleMapsSettings: { apiKey , defaultZoom , hasCustomZoomLevel , maxZoom , defaultCenter , pin , markerClusterOptions ,  } ,  } = useGoogleMapsContext();
    const mapRef = React.useRef();
    const [markerRef1, setMarkerRef] = React.useState({});
    const [center, setCenter] = React.useState(centerProp !== null && centerProp !== void 0 ? centerProp : defaultCenter);
    const [zoom1, setZoom] = React.useState(defaultZoom);
    const [userSharingLocation, setUserSharingLocation] = React.useState(false);
    const useMyLocationControlDiv = React.useRef();
    const zoomControlDiv = React.useRef();
    const prevMarkers = usePrevious(markers);
    const [ReactGoogleMaps] = useGoogleMapsModule();
    const options = swcHelpers.objectSpread({
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
        maxZoom: maxZoom !== null && maxZoom !== void 0 ? maxZoom : DEFAULT_MAX_ZOOM
    }, mapOptions, {
        zoomControl: false
    });
    // Markers that are included for actions like automatic centering, zooming
    const markersWithMapBehavior = React.useMemo(()=>{
        return markers.filter((marker)=>{
            return !!!(marker === null || marker === void 0 ? void 0 : marker.excludedFromMapBehavior);
        });
    }, [
        markers
    ]);
    const onMarkerLoad = (marker, markerId)=>markerId && setMarkerRef((prevState)=>swcHelpers.objectSpread({}, prevState, {
                [markerId]: marker
            })
        )
    ;
    var ref4;
    const handleZoomChanged = React.useCallback(()=>{
        var ref1;
        return mapRef.current && setZoom((ref4 = mapRef === null || mapRef === void 0 ? void 0 : (ref1 = mapRef.current) === null || ref1 === void 0 ? void 0 : ref1.getZoom()) !== null && ref4 !== void 0 ? ref4 : defaultZoom);
    }, [
        defaultZoom
    ]);
    const onHover = React.useCallback((markerId, markerRef)=>onMarkerHover && onMarkerHover(markerId, markerRef[markerId])
    , [
        onMarkerHover
    ]);
    const handleMapClick = React.useCallback((e)=>onMapClick && onMapClick(e)
    , [
        onMapClick
    ]);
    const handleZoomInClick = React.useCallback(()=>{
        var ref, ref2;
        var ref3;
        const currentZoom = (ref3 = mapRef === null || mapRef === void 0 ? void 0 : (ref = mapRef.current) === null || ref === void 0 ? void 0 : ref.getZoom()) !== null && ref3 !== void 0 ? ref3 : 0;
        (ref2 = mapRef.current) === null || ref2 === void 0 ? void 0 : ref2.setZoom(currentZoom + 1);
    }, []);
    const handleZoomOutClick = React.useCallback(()=>{
        var ref, ref5;
        var ref6;
        const currentZoom = (ref6 = mapRef === null || mapRef === void 0 ? void 0 : (ref = mapRef.current) === null || ref === void 0 ? void 0 : ref.getZoom()) !== null && ref6 !== void 0 ? ref6 : 0;
        (ref5 = mapRef.current) === null || ref5 === void 0 ? void 0 : ref5.setZoom(currentZoom - 1);
    }, []);
    const handleUseMyLocationClick = React.useCallback(()=>{
        onClickUseMyLocation && onClickUseMyLocation();
        userLocation1 && mapRef.current && handleCurrentPositionClick(userLocation1);
    }, [
        onClickUseMyLocation,
        userLocation1
    ]);
    const handleCurrentPositionClick = (userLocation)=>{
        if (!userLocation || !mapRef.current) {
            return;
        }
        mapRef.current.panTo(userLocation);
        setZoom(14);
    };
    const handleMarkerClick = React.useCallback((markerId)=>{
        if (!markerId) {
            return;
        }
        const highlightMarkerIdPos = markers.find((marker)=>marker.markerId === markerId
        );
        if (mapRef.current && highlightMarkerIdPos) {
            mapRef.current.panTo(highlightMarkerIdPos.position);
        }
        onMarkerClick && onMarkerClick(markerId);
    }, [
        markers,
        onMarkerClick
    ]);
    const renderMarkers = (Marker, clusterer)=>markers.filter((marker)=>!(marker.position.lat === 0 && marker.position.lng === 0)
        ).map((marker, index)=>/*#__PURE__*/ React.createElement(Marker, swcHelpers.extends({
                title: marker.title,
                key: `${marker.markerId}-${index}`,
                position: marker.position,
                onClick: ()=>handleMarkerClick(marker.markerId)
                ,
                clusterer: clusterer,
                onLoad: (mapsMarker)=>onMarkerLoad(mapsMarker, marker.markerId)
                ,
                onMouseOver: ()=>!marker.inactive && onHover(marker.markerId, markerRef1)
                ,
                onMouseOut: ()=>onHover(null, markerRef1)
            }, getMarkerProps({
                highlight: marker.markerId === highlightMarkerId,
                hovered: !marker.inactive && marker.markerId === hovered,
                inactive: marker.inactive,
                isUserMarker: marker.isUserLocation,
                icon: marker.pin || pin,
                infoWindow: marker.infoWindow
            })))
        )
    ;
    const fitMarkers = React.useCallback(()=>{
        // When there are no markers or too many markers or there is a
        // zoom level defined in the query parameters, zoom out to the default zoom
        if (!markersWithMapBehavior.length || markersWithMapBehavior.length > MAX_MARKERS_AMOUNT || hasCustomZoomLevel) {
            setZoom(defaultZoom);
            setCenter(center !== null && center !== void 0 ? center : defaultCenter);
        } else if (mapRef.current) {
            var ref;
            const bounds = markersWithMapBehavior.reduce((newBounds, marker)=>newBounds.extend(marker.position)
            , new google.maps.LatLngBounds());
            (ref = mapRef.current) === null || ref === void 0 ? void 0 : ref.fitBounds(bounds);
        }
    }, [
        center,
        defaultCenter,
        defaultZoom,
        markersWithMapBehavior,
        hasCustomZoomLevel
    ]);
    const setupControls = React.useCallback(()=>{
        if ((mapOptions === null || mapOptions === void 0 ? void 0 : mapOptions.zoomControl) !== false && zoomControlDiv.current) {
            var ref, ref7;
            (ref7 = (ref = mapRef.current) === null || ref === void 0 ? void 0 : ref.controls[google.maps.ControlPosition.RIGHT_BOTTOM]) === null || ref7 === void 0 ? void 0 : ref7.push(zoomControlDiv.current);
        }
        if (showUseMyLocation && onClickUseMyLocation && useMyLocationControlDiv.current) {
            var ref8, ref9;
            (ref9 = (ref8 = mapRef.current) === null || ref8 === void 0 ? void 0 : ref8.controls[google.maps.ControlPosition.TOP_RIGHT]) === null || ref9 === void 0 ? void 0 : ref9.push(useMyLocationControlDiv.current);
        }
    }, [
        showUseMyLocation,
        onClickUseMyLocation,
        useMyLocationControlDiv,
        mapOptions === null || mapOptions === void 0 ? void 0 : mapOptions.zoomControl,
        zoomControlDiv, 
    ]);
    const onLoad = React.useCallback((map)=>{
        mapRef.current = map;
        setupControls();
        fitMarkers();
    }, [
        setupControls,
        fitMarkers
    ]);
    React.useEffect(()=>setZoom(defaultZoom)
    , [
        defaultZoom
    ]);
    React.useEffect(()=>centerProp && setCenter(centerProp)
    , [
        centerProp
    ]);
    React.useEffect(()=>{
        const activeUserLocation = Boolean(userLocation1);
        if (activeUserLocation && userSharingLocation !== activeUserLocation) {
            setUserSharingLocation(true);
        } else if (!activeUserLocation) {
            setUserSharingLocation(false);
        }
    }, [
        userLocation1,
        userSharingLocation
    ]);
    React.useEffect(()=>{
        if (centerOnUserLocation && userLocation1 && userSharingLocation) {
            const zoom = userLocationZoomLevel !== null && userLocationZoomLevel !== void 0 ? userLocationZoomLevel : defaultZoom;
            setZoom(zoom);
            setCenter(userLocation1);
        } else if (markersWithMapBehavior.length && (!prevMarkers || !isEqualPositionsAndOrder(prevMarkers, markers))) {
            // If `markersUpdateConfig` is provided, set center and zoom according to the config.
            if (markersUpdateConfig && Object.keys(markersUpdateConfig).length > 0) {
                if (markersUpdateConfig.centerToFirst && markersWithMapBehavior[0]) {
                    setCenter(markersWithMapBehavior[0].position);
                }
                if (markersUpdateConfig.zoom !== undefined && !hasCustomZoomLevel) {
                    var ref;
                    (ref = mapRef.current) === null || ref === void 0 ? void 0 : ref.setZoom(markersUpdateConfig.zoom);
                    setZoom(markersUpdateConfig.zoom);
                }
            } else {
                fitMarkers();
            }
        }
    // userLocation - don't want this effect to be trigged as we don't want it rerender and reset the zoom
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        prevMarkers,
        userLocationZoomLevel,
        centerOnUserLocation,
        defaultZoom,
        markers,
        markersWithMapBehavior,
        fitMarkers,
        markersUpdateConfig,
        userSharingLocation, 
    ]);
    React.useEffect(()=>{
        useMyLocationControlDiv.current = document.createElement('div');
        zoomControlDiv.current = document.createElement('div');
    }, []);
    const [ready, setReady] = React.useState(false);
    const scriptLoaded = React.useCallback(()=>setReady(true)
    , []);
    if (!ReactGoogleMaps) {
        return showLoader ? /*#__PURE__*/ React.createElement(LoaderScreen, null) : null;
    }
    const shouldRenderUseMyLocationButton = showUseMyLocation && onClickUseMyLocation;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(ReactGoogleMaps.LoadScript, {
        googleMapsApiKey: apiKey,
        onLoad: scriptLoaded,
        loadingElement: /*#__PURE__*/ React.createElement(LoaderScreen, null)
    }, /*#__PURE__*/ React.createElement(ReactGoogleMaps.GoogleMap, {
        "data-t": "google-map",
        mapContainerClassName: "google-maps__container",
        options: options,
        onLoad: onLoad,
        onClick: handleMapClick,
        onZoomChanged: handleZoomChanged,
        zoom: zoom1,
        center: center,
        id: id
    }, ready && (markerClusterOptions ? /*#__PURE__*/ React.createElement(ReactGoogleMaps.MarkerClusterer, swcHelpers.extends({}, markerClusterOptions), (clusterer)=>/*#__PURE__*/ React.createElement(React.Fragment, null, renderMarkers(ReactGoogleMaps.Marker, clusterer))
    ) : renderMarkers(ReactGoogleMaps.Marker)))), (mapOptions === null || mapOptions === void 0 ? void 0 : mapOptions.zoomControl) !== false && zoomControlDiv.current && /*#__PURE__*/ ReactDOM.createPortal(/*#__PURE__*/ React.createElement("div", {
        className: "google-maps__zoom-control"
    }, /*#__PURE__*/ React.createElement("button", {
        className: "google-maps__zoom-in-button",
        onClick: handleZoomInClick,
        title: (labels === null || labels === void 0 ? void 0 : labels.zoomIn) || DEFAULT_ZOOM_IN_LABEL,
        "aria-label": (labels === null || labels === void 0 ? void 0 : labels.zoomIn) || DEFAULT_ZOOM_IN_LABEL
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "plus"
    })), /*#__PURE__*/ React.createElement("button", {
        className: "google-maps__zoom-out-button",
        onClick: handleZoomOutClick,
        title: (labels === null || labels === void 0 ? void 0 : labels.zoomOut) || DEFAULT_ZOOM_OUT_LABEL,
        "aria-label": (labels === null || labels === void 0 ? void 0 : labels.zoomOut) || DEFAULT_ZOOM_OUT_LABEL
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "minus"
    }))), zoomControlDiv.current), shouldRenderUseMyLocationButton && useMyLocationControlDiv.current && /*#__PURE__*/ ReactDOM.createPortal(/*#__PURE__*/ React.createElement("button", {
        className: "google-maps__center-position-button",
        onClick: handleUseMyLocationClick,
        title: (labels === null || labels === void 0 ? void 0 : labels.shareCurrentLocation) || DEFAULT_SHARE_CURRENT_LOCATION_LABEL,
        "aria-label": (labels === null || labels === void 0 ? void 0 : labels.shareCurrentLocation) || DEFAULT_SHARE_CURRENT_LOCATION_LABEL
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "gps"
    })), useMyLocationControlDiv.current));
};

export { GoogleMaps as default };
//# sourceMappingURL=GoogleMaps.js.map
